/**
 * PreviewExcludedIndicator — compact excluded marker for preview rows.
 *
 * When `row` is given, hovering the icon opens the full PreviewRowHoverCard
 * (used where the row's own hover trigger is scoped to the metric cells, so the
 * icon would otherwise be outside it). Otherwise it's a plain icon — the
 * surrounding row already triggers the card.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Ban } from 'lucide-react';

import { cn } from '@/lib/utils';

import { PreviewRowHoverCard } from './hover';
import type { PreviewRow } from './preview-row.types';

export interface PreviewExcludedIndicatorProps {
  className?: string;
  /** Accessible label — e.g. the matched exclusion condition. */
  title?: string;
  /** When set, hovering the icon opens the full exclusion hover card. */
  row?: PreviewRow | null;
  /** Item name for the hover card header (used with `row`). */
  name?: string;
}

const Icon: FC<{ title: string; className?: string }> = ({ title, className }) => (
  <span
    className={cn(
      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-destructive/10',
      className,
    )}
    aria-label={title}
  >
    <Ban className="h-2.5 w-2.5 text-destructive" strokeWidth={2.5} aria-hidden />
  </span>
);

const PreviewExcludedIndicator: FC<PreviewExcludedIndicatorProps> = ({
  className,
  title = 'Excluded by rule',
  row,
  name,
}) => {
  if (row) {
    return (
      <PreviewRowHoverCard name={name ?? title} row={row} className={cn('inline-flex', className)}>
        <Icon title={title} />
      </PreviewRowHoverCard>
    );
  }
  return <Icon title={title} className={className} />;
};

export default PreviewExcludedIndicator;
